import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';

/**
 * Matches the Full screen / Exit Full screen controls next to BiQ on Analytics.
 * `heightClass` lets callers match a different control row height; the default
 * 40px is what the Global Produce Map strip uses.
 * `compact` renders an icon-only variant (label dropped, square padding) for
 * the title-adjacent control on small screens — see ChartFullscreenTitleControl.
 */
const AnalyticsChartFullscreenButton = ({ isActive, onEnter, onExit, heightClass = 'h-10', compact = false, round = false }) => {
  const { t } = useTranslation();

  // The pill advertises Esc, so Esc has to work. Declared before the early
  // return below — a hook after it would change the hook count between renders.
  useEffect(() => {
    if (!round || !isActive || !onExit) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onExit(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [round, isActive, onExit]);

  if (!onEnter || !onExit) return null;

  // `round`: the Global Produce Map's floating 40px icon button. It sits on map
  // tiles rather than a page background, so it carries the same translucent,
  // blurred surface as the layer rail instead of the flat filter-bar chrome.
  if (round) {
    const label = isActive
      ? t('analytics.exitFullScreen', 'Exit full screen')
      : t('analytics.fullScreen', 'Full screen');

    // Exiting is a labelled pill rather than an icon button: once the chrome is
    // gone it is the only way back, so it advertises itself and the Esc
    // shortcut instead of hiding behind a glyph.
    if (isActive) {
      return (
        <button
          type="button"
          onClick={onExit}
          aria-label={label}
          className="flex h-[40px] shrink-0 cursor-pointer items-center bg-[#F6D2CD] transition-opacity hover:opacity-90 max-lg:w-[40px] max-lg:justify-center max-lg:rounded-[8px] lg:gap-[10px] lg:rounded-full lg:pl-[14px] lg:pr-[6px]"
        >
          <Icon icon="ant-design:fullscreen-exit-outlined" width="16" height="16" className="shrink-0 text-[#5F6875]" />
          {/* Phones get the icon alone — no room for the label, and no ESC key
              to hint at. Desktop keeps the labelled pill. */}
          <span className="hidden font-['Inter'] text-[14px] font-bold whitespace-nowrap text-[#162032] lg:inline">{label}</span>
          <span className="hidden h-[22px] items-center rounded-[7px] bg-[#F3F5F3] px-[8px] font-['Inter'] text-[11px] font-bold text-[#7F8591] lg:flex">
            {t('analytics.escKey', 'ESC')}
          </span>
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={isActive ? onExit : onEnter}
        aria-label={label}
        title={label}
        className={`flex h-[40px] w-[40px] shrink-0 cursor-pointer items-center justify-center rounded-full border border-solid transition-colors hover:border-bloom-green ${floatingSurfaceClass(94)}`}
      >
        <Icon icon="ant-design:fullscreen-outlined" width="16" height="16" className="text-bloom-green shrink-0" />
      </button>
    );
  }

  const compactBtn = compact
    ? 'max-sm:w-10 max-sm:px-0 max-sm:gap-0 justify-center sm:px-4'
    : 'px-4';
  const labelClass = compact ? 'hidden sm:inline' : undefined;

  if (isActive) {
    return (
      <button
        type="button"
        onClick={onExit}
        aria-label={t('analytics.exitFullScreen', 'Exit full screen')}
        className={`${heightClass} ${compactBtn} rounded-lg border border-[#E8EBE9] dark:border-[#3a3c3b] bg-[#FBE4C6] text-[#282A29] text-sm font-medium transition-colors flex items-center gap-2 cursor-pointer shrink-0 whitespace-nowrap`}
      >
        <Icon icon="ant-design:fullscreen-outlined" width={compact ? '18' : '16'} height={compact ? '18' : '16'} className="text-bloom-green shrink-0" />
        <span className={labelClass}>{t('analytics.exitFullScreen', 'Exit full screen')}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onEnter}
      aria-label={t('analytics.fullScreen', 'Full screen')}
      className={`${heightClass} ${compactBtn} rounded-lg border border-[#E8EBE9] dark:border-[#3a3c3b] bg-white dark:bg-[#191919] text-[#282A29] dark:text-white text-sm font-medium hover:border-bloom-green/60 transition-colors flex items-center gap-2 cursor-pointer shrink-0 whitespace-nowrap`}
    >
      <Icon icon="ant-design:fullscreen-outlined" width={compact ? '18' : '16'} height={compact ? '18' : '16'} className="text-bloom-green shrink-0" />
      <span className={labelClass}>{t('analytics.fullScreen', 'Full screen')}</span>
    </button>
  );
};

export default AnalyticsChartFullscreenButton;
